import React, { useEffect, useMemo, useState } from "react"
import { Tag, Button, Flex, Dropdown, Spin, Space, Tooltip } from "antd"
import { CloseOutlined, EditOutlined, EyeOutlined, MoreOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import PropTypes from "prop-types"
import { LocationIcon } from "icons"
import DataTable from "ui/DataTable"
import { Text } from "ui/Typography"
import styles from "./index.module.css"
import { EmptyState } from "pagesV2/pro/Experiences/Home/TabContent"
import Logo from "components/common/Logo"
import CustomPagination from "components/common/CustomPagination"
import { SingleTicketIcon } from "ui/Icon/DSIcon/SingleTicket"
import { MultiTicketsIcon } from "ui/Icon/DSIcon/MultiTickets"
import { RentalIcon } from "ui/Icon/DSIcon/RentalIcon"
import { EXPERIENCE_STATES, EXPERIENCE_TYPES } from "constants/experiences"
import { routes } from "router"
import { useNavigate } from "react-router-dom"
import { getActionConditions, getDropdownItems, formatExperienceTerms } from "utils/experience"
import { getMenuConfig } from "ui/ExperienceCard/MenuConfig"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
export const ListingTable = ({
  onDelete,
  onDeactivate,
  onDuplicate,
  onShare,
  onPublish,
  onEditCategory,
  onActionComplete,
  onAddNewExperience,
  activeListingTab,
  data,
  loading,
  metadata,
  showPagination,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onView,
  onMoreAction,
  onSortChange,
  isDuplicateInProgress,
  duplicatingTripId,
  processingAction,
  processingTripId,
  onBulkActivate,
  onBulkDeactivate,
  onBulkDuplicate,
  onBulkDelete,
}) => {
  const { t } = useTranslation()
  const { hasPermission } = usePermissions()
  const uiType = activeListingTab === "unpublished" ? "deactivated" : activeListingTab
  const navigate = useNavigate()
  const [selectedRowKeys, setSelectedRowKeys] = useState([])

  useEffect(() => {
    setSelectedRowKeys([])
  }, [activeListingTab, page, pageSize])

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys)
  }
  const handleEdit = (record) => {
    const isDraft = record.status === EXPERIENCE_STATES.draft
    const listingType = record.experience_type === EXPERIENCE_TYPES.ticketed ? EXPERIENCE_TYPES.ticketed : EXPERIENCE_TYPES.private
    if (isDraft) {
      navigate(`${routes.proNewExperienceV2Path({ id: record?.id })}?type=${listingType}`)
    } else {
      const routeType = record.status === EXPERIENCE_STATES.deactivated ? "deactivated" : "published"
      const baseUrl = routes.proEditExperienceV2Path({ id: record?.id })
      navigate(`${baseUrl}?route=${routeType}&type=${listingType}`)
    }
  }
  const handlePreviewClick = (record) => {
    const isDraft = record.status === EXPERIENCE_STATES.draft
    if (isDraft) {
      navigate(routes.proNewExperienceV2PreviewPath({ id: record?.id }))
    } else {
      const routeType = record.status === EXPERIENCE_STATES.deactivated ? "deactivated" : "published"
      const baseUrl = routes.proEditExperienceV2PreviewPath({ id: record?.id })
      navigate(`${baseUrl}&readOnlyPreview=true&route=${routeType}`)
    }
  }

  const getExperienceTypeIcon = (type) => {
    switch (String(type || "").toLowerCase()) {
      case "ticketed":
        return <MultiTicketsIcon fill="var(--color-primary)" width={20} height={20} />
      case "rental":
      case "rentals":
        return <RentalIcon fill="var(--color-primary)" />
      case "private":
      default:
        return <SingleTicketIcon fill="var(--color-primary)" width={20} height={20} />
    }
  }

  const handleTableChange = (_, __, sorter) => {
    if (!onSortChange) return
    if (!sorter.field || !sorter.order) {
      onSortChange(null)
      return
    }
    const fieldMap = { experienceType: "experience_type" }
    const field = fieldMap[sorter.field] ?? sorter.field
    const direction = sorter.order === "ascend" ? "asc" : "desc"
    onSortChange({ sort_by: field, order: direction })
  }

  const handleDropdownClick = (info, dropdownItems) => {
    const item = dropdownItems?.find((i) => i.key === info.key)
    if (item && typeof item.onClick === "function") {
      item.onClick()
    }
  }

  const getPricingTypeSuffix = (pricingType) => {
    switch (pricingType) {
      case "charge_flat_rate":
        return "/flat"
      case "charge_per_person":
        return "/person"
      case "set_minimum_rate":
        return "/min"
      default:
        return ""
    }
  }

  const columns = useMemo(
    () => [
      {
        title: t("equipment.listing_table.experience_name"),
        dataIndex: "title",
        key: "title",
        sorter: true,
        render: (text, record) => {
          // Find photo with position 0, or use the first photo as fallback
          const positionZeroPhoto = record.photos?.find((photo) => photo.position === 0) || record.photos?.[0]

          return (
            <Flex align="center" gap={8} className={styles.experienceNameContainer}>
              {positionZeroPhoto ? (
                <img
                  src={positionZeroPhoto.preview_url}
                  alt={text}
                  className={styles.experiencePhoto}
                  onError={(e) => {
                    e.target.style.display = "none"
                    e.target.nextSibling.style.display = "flex"
                  }}
                />
              ) : (
                <div className={styles.experiencePhotoPlaceholder}>
                  <Logo className="opacity-50" alt="Logo" width={30} height={30} style={{ filter: "grayscale(1)" }} />
                </div>
              )}
              <Text
                className={styles.experienceNameText}
                title={text?.length > 10 ? text : undefined}
                onClick={() => handleEdit(record)}
                style={{ cursor: "pointer", color: "var(--color-primary)" }}
              >
                {text?.length > 10 ? `${text.substring(0, 10)}...` : text}
              </Text>
            </Flex>
          )
        }
      },
      {
        title: t("equipment.listing_table.experience_type"),
        dataIndex: "experienceType",
        key: "experienceType",
        render: (_, record) => {
          const rawType = record?.trip_type || record?.experience_type || record?.experienceType || record?.type || "private"
          const normalizedType = String(rawType).toLowerCase()
          const label = normalizedType.charAt(0).toUpperCase() + normalizedType.slice(1)
          return (
            <Flex align="center" gap={8}>
              {getExperienceTypeIcon(normalizedType)}
              <Text style={{ color: "var(--color-text-primary)" }}>{label}</Text>
            </Flex>
          )
        }
      },
      {
        title: t("equipment.listing_table.published"),
        dataIndex: "published",
        key: "published",
        render: (_, record) => {
          const isPublished = record.status === EXPERIENCE_STATES.published
          const published = [
            ...(record.publish_on_platform ? [t("global.ds_marketplace")] : []),
            ...(record.publish_on_guides_site ? [t("global.frontend", { defaultValue: "Frontend" })] : [])
          ]
          return (
            <Flex vertical gap={2}>
              {isPublished &&
                published.map((platform) => (
                  <Text key={platform} style={{ color: "var(--color-text-primary)" }}>
                    {platform}
                  </Text>
                ))}
            </Flex>
          )
        }
      },
      {
        title: t("equipment.listing_table.categories"),
        dataIndex: "categories",
        key: "categories",
        render: (categories) => {
          if (!Array.isArray(categories) || categories.length === 0) return null
          return (
            <Flex gap={4} wrap="wrap">
              {categories.map((category, index) => {
                const label = typeof category === "object" ? (category?.name ?? category?.label) : String(category)
                const key = typeof category === "object" ? (category?.id ?? index) : `${category}-${index}`
                return (
                  <Tag
                    key={key}
                    style={{
                      background: "var(--color-primary-surface)",
                      color: "var(--color-primary)",
                      border: "1px solid var(--color-primary)",
                      borderRadius: "999px",
                      padding: "2px 10px",
                      fontSize: "var(--font-size-s)"
                    }}
                  >
                    {label}
                  </Tag>
                )
              })}
            </Flex>
          )
        }
      },
      {
        title: t("equipment.listing_table.location"),
        dataIndex: "address",
        key: "address",
        sorter: true,
        render: (location) => (
          <Flex align="center" gap={6}>
            {location && <LocationIcon style={{ width: 16, height: 16, color: "var(--color-text-secondary)" }} />}
            <Text style={{ color: "var(--color-text-primary)" }} title={location?.length > 10 ? location : undefined}>
              {location?.length > 10 ? `${location.substring(0, 10)}...` : location}
            </Text>
          </Flex>
        )
      },
      {
        title: t("equipment.listing_table.price"),
        dataIndex: "min_price",
        key: "min_price",
        sorter: true,
        render: (price, record) => {
          const suffix = getPricingTypeSuffix(record.pricing_type)
          return (
            <Text style={{ color: "var(--color-text-secondary)" }}>
              from{" "}
              <span style={{ color: "var(--color-primary)", fontWeight: 600 }}>${Number.parseFloat(price || 0)}</span>
              {suffix && <span style={{ color: "var(--color-text-secondary)" }}> {suffix}</span>}
            </Text>
          )
        }
      },
      {
        title: t("equipment.listing_table.terms", { defaultValue: "Terms" }),
        dataIndex: "available_hours_range",
        key: "available_hours_range",
        render: (_range, record) => (
          <Text style={{ color: "var(--color-text-primary)" }}>{formatExperienceTerms(record, t)}</Text>
        )
      },
      {
        title: t("equipment.listing_table.actions"),
        key: "actions",
        width: 120,
        render: (_, record) => {
          const isRowDuplicating = duplicatingTripId === record.id
          const isRowProcessing = processingTripId === record.id
          const rowProcessingAction = isRowProcessing ? processingAction : null
          const conditions = getActionConditions(record.status)

          // Create menuConfig per row with the specific record
          const rowMenuConfig = getMenuConfig(t, {
            onShare: () => onShare?.(record),
            onDuplicate: () => {
              return onDuplicate?.(record, onActionComplete)
            },
            onDeactivate: () => onDeactivate?.(record, onActionComplete),
            onDelete: () => onDelete?.(record, onActionComplete),
            onPublish: () => onPublish?.(record, onActionComplete),
            onEditCategory: () => onEditCategory?.(record)
          })

          const dropdownItems = getDropdownItems(rowMenuConfig, conditions)

          const isDeactivated = record.status === EXPERIENCE_STATES.deactivated
          let filteredDropdownItems = isDeactivated
            ? dropdownItems.filter((item) => item.key === "publish" || item.key === "delete" || item.key === "duplicate")
            : dropdownItems

          filteredDropdownItems = filteredDropdownItems.filter((item) => {
            switch (item.key) {
              case "deactivate":
                return hasPermission(MODULES.EXPERIENCES, ACTIONS.EDIT)
              case "publish":
                return record?.can_edit
              case "delete":
                return record?.can_edit
              case "duplicate":
                return hasPermission(MODULES.EXPERIENCES, ACTIONS.CREATE)
              case "edit_category":
                return record?.can_edit && record.status === EXPERIENCE_STATES.published
              case "share":
              default:
                return true
            }
          })

          const menuItems = filteredDropdownItems.map((item) => ({
            ...item,
            disabled: isDuplicateInProgress || isRowProcessing,
            label:
              (item.key === "duplicate" && isRowDuplicating) || item.key === rowProcessingAction ? (
                <Space size={8}>
                  <Spin size="small" />
                  {item.label}
                </Space>
              ) : (
                item.label
              )
          }))

          return (
            <Flex gap={8}>
              <Tooltip title={record.status === EXPERIENCE_STATES.draft ? t("global.continue_creating") : t("global.edit")}>
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                  className={styles.iconButton}
                  disabled={isDuplicateInProgress || isRowProcessing}
                />
              </Tooltip>
              {record.status !== EXPERIENCE_STATES.draft && (
                <Tooltip title={t("global.preview")}>
                  <Button
                    type="text"
                    icon={<EyeOutlined />}
                    onClick={() => handlePreviewClick(record)}
                    className={styles.iconButton}
                    disabled={isDuplicateInProgress || isRowProcessing}
                  />
                </Tooltip>
              )}

              <Dropdown
                menu={{
                  items: menuItems,
                  onClick: (info) => handleDropdownClick(info, menuItems)
                }}
                trigger={["click"]}
                placement="bottomRight"
              >
                <Button
                  type="text"
                  icon={<MoreOutlined />}
                  className={styles.iconButton}
                  disabled={isDuplicateInProgress || isRowProcessing}
                  loading={isRowDuplicating || isRowProcessing}
                />
              </Dropdown>
            </Flex>
          )
        }
      }
    ],
    [
      t,
      hasPermission,
      handleEdit,
      handlePreviewClick,
      onShare,
      onDuplicate,
      onDeactivate,
      onDelete,
      onPublish,
      onEditCategory,
      onActionComplete
    ]
  )
  // Show the empty state only when a fetch has settled with no rows — never while
  // loading, otherwise a sort/pagination refetch (which briefly clears the data)
  // would flash "no experiences".
  const emptyState = () => {
    if (!loading && data?.length === 0) {
      return <EmptyState type={uiType} onAddNewExperience={onAddNewExperience} />
    }
    return null
  }

  const selectedCount = selectedRowKeys.length
  const selectedRecords = data.filter((record) => selectedRowKeys.includes(record.id))

  const showActivate = activeListingTab === "unpublished"
  const showDeactivate = activeListingTab === "published" || activeListingTab === ""

  return (
    <div className={styles.container}>
      {selectedCount > 0 && (
        <div className={styles.bulkBar}>
          <Flex align="center" gap={12}>
            <Text fw={500}>{selectedCount} selected</Text>
            {showActivate && (
              <Button
                size="large"
                color="primary"
                variant="outlined"
                onClick={() => onBulkActivate?.(selectedRecords, () => setSelectedRowKeys([]))}
              >
                {t("global.publish")}
              </Button>
            )}
            <Button
              size="large"
              color="primary"
              variant="outlined"
              onClick={() => onBulkDuplicate?.(selectedRecords, () => setSelectedRowKeys([]))}
            >
              {t("global.duplicate", { defaultValue: "Duplicate" })}
            </Button>
            {showDeactivate && (
              <Button
                size="large"
                color="danger"
                variant="outlined"
                onClick={() => onBulkDeactivate?.(selectedRecords, () => setSelectedRowKeys([]))}
              >
                {t("global.deactivate", { defaultValue: "Deactivate" })}
              </Button>
            )}
            <Button
              size="large"
              color="danger"
              variant="outlined"
              onClick={() => onBulkDelete?.(selectedRecords, () => setSelectedRowKeys([]))}
            >
              {t("global.delete", { defaultValue: "Delete" })}
            </Button>
          </Flex>
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={() => setSelectedRowKeys([])}
            className={styles.bulkBarClose}
          />
        </div>
      )}
      {emptyState()}
      {/* Keep the table mounted while loading so the sort refetch shows an overlay
          spinner (not a full unmount) and antd retains the active sort-column icon. */}
      {(loading || data.length > 0) && (
        <DataTable columns={columns} dataSource={data} loading={loading} pagination={false} rowKey="id" rowSelection={rowSelection} onChange={handleTableChange} className={styles.listingTable} />
      )}
      {showPagination && metadata?.total_results > 10 && (
        <CustomPagination
          metadata={{
            page: metadata?.page || 1,
            total_pages: metadata?.total_pages || 1,
            next_page: metadata?.next_page,
            prev_page: metadata?.prev_page
          }}
          pageSize={pageSize}
          total={metadata?.total_results || 0}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </div>
  )
}

ListingTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      address: PropTypes.string,
      min_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      max_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      publish_on_platform: PropTypes.bool,
      publish_on_guides_site: PropTypes.bool,
      can_edit: PropTypes.bool
    })
  ).isRequired,
  loading: PropTypes.bool,
  metadata: PropTypes.shape({
    page: PropTypes.number,
    total_pages: PropTypes.number,
    total_results: PropTypes.number,
    next_page: PropTypes.number,
    prev_page: PropTypes.number
  }),
  showPagination: PropTypes.bool,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  onEdit: PropTypes.func,
  onView: PropTypes.func,
  onMoreAction: PropTypes.func,
  onSortChange: PropTypes.func,
  onShare: PropTypes.func,
  onDuplicate: PropTypes.func,
  onDeactivate: PropTypes.func,
  onDelete: PropTypes.func,
  onPublish: PropTypes.func,
  onEditCategory: PropTypes.func,
  onActionComplete: PropTypes.func,
  onAddNewExperience: PropTypes.func,
  activeListingTab: PropTypes.string,
  onBulkActivate: PropTypes.func,
  onBulkDeactivate: PropTypes.func,
  onBulkDuplicate: PropTypes.func,
  onBulkDelete: PropTypes.func,
  isDuplicateInProgress: PropTypes.bool,
  duplicatingTripId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  processingAction: PropTypes.string,
  processingTripId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

ListingTable.defaultProps = {
  loading: false,
  metadata: {},
  showPagination: false,
  page: 1,
  pageSize: 10,
  onPageChange: () => {},
  onPageSizeChange: () => {},
  onEdit: () => {},
  onView: () => {},
  onMoreAction: () => {},
  onSortChange: null,
  onShare: () => {},
  onDuplicate: () => {},
  onDeactivate: () => {},
  onDelete: () => {},
  onPublish: () => {},
  onEditCategory: () => {},
  onActionComplete: () => {},
  onAddNewExperience: () => {},
  activeListingTab: "",
  onBulkActivate: undefined,
  onBulkDeactivate: undefined,
  onBulkDuplicate: undefined,
  onBulkDelete: undefined,
  isDuplicateInProgress: false,
  duplicatingTripId: null,
  processingAction: null,
  processingTripId: null
}
